import * as React from 'react'
import { useParams } from 'react-router-dom'
import { Box, Container, Divider, GlobalStyles, Typography } from '@mui/material'
import TitleBar from 'components/TitleBar'
import DeliveryDatePicker from 'components/DeliveryDatePicker'
import Statistics from './components/Statistics'
import Filters from './components/Filters'
import OrderGrid from './components/OrderGrid'
import { OrdersContext } from 'contexts/ordersContext'

/**
 * Overview component for displaying statistics and order cards
 */
const Overview = () => {
  const [filters, setFilters] = React.useState({ search: '', status: 'pending' })
  const { orders, fetchOrders } = React.useContext(OrdersContext)
  const { deliveryDate } = useParams()

  // Fetching orders
  React.useEffect(() => {
    fetchOrders(deliveryDate)
  }, [deliveryDate])

  // Filtering data
  const filteredOrders = orders.filter((order) => {
    const result = []

    if (filters.search !== '') {
      const searchText = filters.search.toLowerCase()
      const search = (obj) => {
        if (!obj || typeof obj !== 'object') return false

        return Object.values(obj).some((value) => {
          if (typeof value === 'object') return search(value)
          if (typeof value === 'string') return value.toLowerCase().includes(searchText)
          return false
        })
      }

      result.push(search(order))
    }

    if (filters.status !== 'all') {
      switch (filters.status) {
        case 'pending':
          result.push(!order.completed)
          break

        case 'completed':
          result.push(order.completed)
          break
      }
    }
    
    return result.every(Boolean)
  })

  const pickupOrders = filteredOrders.filter((order) => order.type === 'pickup')
  const deliveryOrders = filteredOrders.filter((order) => order.type === 'delivery')

  // Calculating stats
  const summary = orders.reduce(
    (acc, order) => {
      acc.totalMeals += order.mealsNo || 0
      acc.totalWraps += order.wrapsNo || 0
      acc.totalRevenue += order.total - order.taxes.reduce((sum, tax) => sum + tax.amount, 0)
      acc.totalOrders += 1

      if (order.type === 'pickup') acc.totalPickups += 1
      if (order.type === 'delivery') acc.totalDeliveries += 1

      return acc
    },
    {
      totalMeals: 0,
      totalWraps: 0,
      totalRevenue: 0,
      totalPickups: 0,
      totalDeliveries: 0,
      totalOrders: 0,
    }
  )

  const stats = [
    { label: 'Total Meals', value: summary.totalMeals },
    { label: 'Total Wraps', value: summary.totalWraps },
    { label: 'Total Revenue', value: Math.round(summary.totalRevenue * 100) / 100 },
    { label: 'Total Pickups', value: summary.totalPickups },
    { label: 'Total Deliveries', value: summary.totalDeliveries },
    { label: 'Total Orders', value: summary.totalOrders },
  ]

  return (
    <Box>
      <GlobalStyles
        styles={{
          '@media print': {
            '.MuiMasonry-root': {
              flexFlow: 'row wrap !important',
              alignItems: 'flex-start',
              height: 'auto !important'
            },
            '.MuiMasonry-root > div': {
              width: 'calc(33.333% - calc(3 * var(--mui-spacing))) !important',
              breakInside: 'avoid'
            },
            '.MuiMasonry-root > span': {
              display: 'none'
            },
          },
        }}
      />
      <TitleBar title="Overview" actions={[<DeliveryDatePicker />]} />
      <Container maxWidth="xl">
        <Statistics stats={stats} />
        
        <Divider sx={{ displayPrint: 'none' }} />
        
        <Filters filters={filters} setFilters={setFilters} />

        <Divider sx={{ displayPrint: 'none' }} />

        <Box sx={{ mt: 6, mb: 3 }}>
          <Typography variant="h6" sx={{ display: 'none', displayPrint: 'block', fontWeight: 'fontWeightMedium', mb: 1 }}>Pickup Orders</Typography>
          <OrderGrid orders={pickupOrders} noOrderMessage="No pickup orders found." />
        </Box>

        <Divider sx={{ displayPrint: 'none' }} />

        <Box sx={{ mt: 6, mb: 3 }}>
          <Typography variant="h6" sx={{ display: 'none', displayPrint: 'block', fontWeight: 'fontWeightMedium', mb: 1, breakBefore: 'page' }}>Delivery Orders</Typography>
          <OrderGrid orders={deliveryOrders} noOrderMessage="No delivery orders found." />
        </Box>
      </Container>
    </Box>
  )
}

export default Overview
